import React, { FormEvent, useState, useEffect } from 'react';
import styles from './ExistingUser.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import { PasswordInputField } from 'components/helpers/Atoms/FormElements/PasswordInputField';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { signIn } from 'next-auth/react';
import { getCookie, removeCookie } from 'lib/utils/cookies';
import router from 'next/router';
import { useQueryStringParameters } from 'lib/hooks/browser/useQueryString';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import { SOCIAL_OAUTH_COOKIE_NAME, SocialOAuthDetails } from 'src/pages/api/auth/[...nextauth]';

/**
 * Decodes social OAuth details from Base64 cookie value
 * Uses browser-native atob() for client-side decoding
 */
function decodeSocialOAuthCookie(cookieValue: string): SocialOAuthDetails | null {
  try {
    // Use atob for browser-side Base64 decoding
    const decoded = atob(cookieValue);
    return JSON.parse(decoded) as SocialOAuthDetails;
  } catch (error) {
    console.error('Error decoding social OAuth cookie:', error);
    return null;
  }
}

/**
 * Clears the social OAuth details cookie
 */
export function clearSocialOAuthCookie(): void {
  removeCookie(SOCIAL_OAUTH_COOKIE_NAME);
}

/**
 * Reads and returns the social OAuth details from cookie
 */
export function getSocialOAuthDetails(): SocialOAuthDetails | null {
  const cookieValue = getCookie(SOCIAL_OAUTH_COOKIE_NAME);
  if (!cookieValue) return null;
  return decodeSocialOAuthCookie(cookieValue);
}

export interface ExistingUserProps {
  alreadyMemberHeading?: React.ReactNode;
  alreadyMemberDescription?: React.ReactNode;
  verifyEmailLabel?: React.ReactNode;
  verifyEmailPlaceholder?: string;
  socialConnectSubmitText?: React.ReactNode;
  cancelLink?: React.ReactNode;
  forgotPasswordLink?: React.ReactNode;
  passwordLabel: React.ReactNode;
  passwordPlaceholderText: string;
}

const errorMessages: Record<string, string> = {
  AUTHENTICATION_FAILED: 'Components.ExistingUser.AuthenticationFailed',
  MISSING_CREDENTIALS: 'Components.ExistingUser.GenericError',
  GENERIC_ERROR: 'Components.ExistingUser.GenericError',
} as const;

const FORM_FIELD_IDS = {
  EMAIL: 'existing-user-email',
  PASSWORD: 'existing-user-password',
} as const;

export default function ExistingUser(props: ExistingUserProps) {
  const {
    alreadyMemberHeading,
    alreadyMemberDescription,
    verifyEmailLabel,
    verifyEmailPlaceholder,
    socialConnectSubmitText,
    cancelLink,
    forgotPasswordLink,
    passwordLabel,
    passwordPlaceholderText,
  } = props;
  const { getDictionaryValue } = useDictionary();
  const [, , queryParamError] = useQueryStringParameters('returnUrl', 'callbackUrl', 'error');
  const { setFieldError, clearAllErrors, getFieldError, hasErrors } = useFormErrors();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [error, setError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<'google' | 'facebook' | null>(null);
  const [idToken, setIdToken] = useState('');
  const [successRedirectUrl, setSuccessRedirectUrl] = useState<string>('/');

  // Initialize error from query params
  useEffect(() => {
    if (queryParamError) {
      const errorKey = errorMessages[queryParamError];
      setError(
        (errorKey ? getDictionaryValue(errorKey) : '') ||
          getDictionaryValue(errorMessages.GENERIC_ERROR) ||
          queryParamError
      );
    }
  }, [queryParamError, getDictionaryValue]);

  // Read OAuth information from cookie on mount
  useEffect(() => {
    const oauthDetails = getSocialOAuthDetails();
    if (oauthDetails && !oauthDetails.isNewUser) {
      // Only use cookie data for existing users (social connect)
      setEmail(oauthDetails.email);
      setOauthProvider(oauthDetails.provider);
      setIdToken(oauthDetails.idToken);
      setSuccessRedirectUrl(oauthDetails.redirectUrl || '/');
      // Clear cookie after storing data in state
      clearSocialOAuthCookie();
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setHasAttemptedSubmit(true);
    clearAllErrors();

    let hasValidationErrors = false;

    // Validate email
    if (!email.trim()) {
      const emailRequiredMessage = getDictionaryValue(
        'Components.ExistingUser.EmailRequired',
        'Please verify your email address.'
      );
      setFieldError(FORM_FIELD_IDS.EMAIL, emailRequiredMessage);
      hasValidationErrors = true;
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        const invalidEmailMessage = getDictionaryValue(
          'Components.ExistingUser.InvalidEmail',
          'Please enter a valid email address.'
        );
        setFieldError(FORM_FIELD_IDS.EMAIL, invalidEmailMessage);
        hasValidationErrors = true;
      }
    }

    // Validate password
    if (!password.trim()) {
      const passwordRequiredMessage = getDictionaryValue(
        'Components.ExistingUser.PasswordRequired',
        'Password is required.'
      );
      setFieldError(FORM_FIELD_IDS.PASSWORD, passwordRequiredMessage);
      hasValidationErrors = true;
    }

    // Show form-level error banner if there are validation errors
    if (hasValidationErrors) {
      const missingFieldsMessage = getDictionaryValue(
        'Components.ExistingUser.EmptyFieldsSubmission',
        'There was an error connecting your account.| Please check the below errors and try again.'
      );
      setError(missingFieldsMessage);
      setDisableSubmit(false);
      return;
    }

    setDisableSubmit(true);

    try {
      // Use social_connect provider for both login and signup flows
      // This authenticates with password and links the federation in one step

      const signInResponse = await signIn('social_connect', {
        username: email,
        password: password,
        provider: oauthProvider,
        providerUserToken: idToken,
        redirect: false,
      });

      if (signInResponse?.error) {
        // Clear field errors when API error occurs (these are form-level errors)
        clearAllErrors();
        const errorKey = errorMessages[signInResponse.error];
        const authenticationFailedMessage =
          getDictionaryValue(errorMessages.AUTHENTICATION_FAILED) ||
          'Authentication failed. Please check your password.';
        setError(errorKey ? getDictionaryValue(errorKey) : authenticationFailedMessage);
        setDisableSubmit(false);
        return;
      }

      // Clear OAuth cookies on success
      clearSocialOAuthCookie();
      removeCookie('fromSignupPage');

      // Redirect to the stored success URL from the cookie
      router.push(successRedirectUrl);
    } catch (err) {
      console.error('Existing user login error:', err);
      setError(
        getDictionaryValue(errorMessages.GENERIC_ERROR) ||
          'An unexpected error occurred. Please try again later.'
      );
      setDisableSubmit(false);
    }
  };

  return (
    <div className={styles.container} data-component="authorable/shared/userprofile/existinguser">
      <div className={styles.existingUserWrapper}>
        {alreadyMemberHeading && (
          <div className={styles.formContentHeader}>
            <div className={styles.headerContent}>
              <h1 className={styles.formContentHeaderTitle}>{alreadyMemberHeading}</h1>
            </div>
            <hr className={styles.divider} />
          </div>
        )}

        <div className={styles.formContent}>
          {error && <MessageBanner type="error" message={error} autoFocus />}

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {oauthProvider && alreadyMemberDescription && (
              <div className={styles.oauthEmailDisplay}>
                <SvgIcon
                  fill="none"
                  icon={oauthProvider}
                  size="sm"
                  className={styles.oauthProviderIcon}
                />
                <span className={styles.oauthEmailDisplayText}>{alreadyMemberDescription}</span>
              </div>
            )}
            <div className={styles.requiredField}>
              {getDictionaryValue('Components.MyAccount.Required') || '* Required'}
            </div>
            <div className={styles.formFieldsContainer}>
              <FormField
                label={verifyEmailLabel}
                labelFor={FORM_FIELD_IDS.EMAIL}
                error={
                  hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.EMAIL)
                    ? (getFieldError(FORM_FIELD_IDS.EMAIL) as string)
                    : !hasErrors
                      ? error
                      : undefined
                }
                showInlineError={hasAttemptedSubmit && !!getFieldError(FORM_FIELD_IDS.EMAIL)}
                required
              >
                <TextInput
                  id={FORM_FIELD_IDS.EMAIL}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={verifyEmailPlaceholder}
                  error={
                    hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.EMAIL)
                      ? true
                      : !hasErrors
                        ? !!error
                        : undefined
                  }
                />
              </FormField>
              <PasswordInputField
                id={FORM_FIELD_IDS.PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                label={passwordLabel}
                placeholder={passwordPlaceholderText}
                showInlineError={hasAttemptedSubmit && !!getFieldError(FORM_FIELD_IDS.PASSWORD)}
                error={
                  hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.PASSWORD)
                    ? (getFieldError(FORM_FIELD_IDS.PASSWORD) as string)
                    : !hasErrors
                      ? error
                      : undefined
                }
                required
              />
              {forgotPasswordLink && (
                <div className={styles.forgotPasswordLink}>{forgotPasswordLink}</div>
              )}
            </div>

            <div className={styles.buttonContainer}>
              <CtaButton
                ctaStyle="filled"
                ctaVariant="brand"
                type="submit"
                disabled={disableSubmit}
              >
                {socialConnectSubmitText}
              </CtaButton>
              {cancelLink && <div className={styles.cancelLink}>{cancelLink}</div>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
